import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCcw, EyeOff } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useDemoMode } from "@/hooks/useDemoMode";

export default function Settings() {
  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Configuratie</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Instellingen</h1>
      </header>

      <Tabs defaultValue="orderstatus" data-testid="settings-tabs">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 h-auto">
          <TabsTrigger value="orderstatus" data-testid="tab-orderstatus">Orderstatus</TabsTrigger>
          <TabsTrigger value="betaalmethodes" data-testid="tab-betaalmethodes">Betaalmethodes</TabsTrigger>
          <TabsTrigger value="vaste-kosten" data-testid="tab-vaste-kosten">Vaste kosten</TabsTrigger>
          <TabsTrigger value="boekhoudkost" data-testid="tab-boekhoudkost">Boekhoudkost</TabsTrigger>
          <TabsTrigger value="demo-modus" data-testid="tab-demo-modus">Demo-modus</TabsTrigger>
        </TabsList>
        <TabsContent value="orderstatus"><OrderstatusSettings /></TabsContent>
        <TabsContent value="betaalmethodes"><BetaalmethodesSettings /></TabsContent>
        <TabsContent value="vaste-kosten"><VasteKostenSettings /></TabsContent>
        <TabsContent value="boekhoudkost"><BoekhoudkostSettings /></TabsContent>
        <TabsContent value="demo-modus"><DemoModusSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function DemoModusSettings() {
  const { user } = useAuth();
  const { active, setDemoMode } = useDemoMode();
  const [busy, setBusy] = useState(false);
  const isTrueOwner = !!user?.is_true_owner;

  const toggle = async (next) => {
    setBusy(true);
    try {
      await setDemoMode(next);
      toast.success(next ? "Demo-modus AAN — publiek ziet fictieve data" : "Demo-modus UIT — echte data zichtbaar voor alle gebruikers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon demo-modus niet wisselen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200 p-6 rounded-lg space-y-6" data-testid="demo-modus-card">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
          <EyeOff className="h-6 w-6 text-amber-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900">Demo-modus voor publiek gebruik</h2>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Zet aan als je de app openbaar wilt tonen (bv. tijdens de Emergent builder wedstrijd). Alle bezoekers <b>behalve jij</b> zien dan fictieve klantnamen, e-mails, telefoonnummers en adressen. Bedragen zijn verborgen; percentages, trends en aantallen blijven zichtbaar. CSV-export en AI-chat worden geblokkeerd voor iedereen behalve jou.
          </p>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-900">Status</p>
          <p className={`text-sm mt-0.5 ${active ? "text-amber-700" : "text-slate-500"}`}>
            {active ? "AAN — publiek ziet dummy data" : "UIT — alle ingelogde gebruikers zien echte data"}
          </p>
        </div>
        <Switch
          checked={active}
          onCheckedChange={toggle}
          disabled={busy || !isTrueOwner}
          data-testid="demo-mode-switch"
          className="scale-125"
        />
      </div>

      {!isTrueOwner && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="demo-mode-permission-hint">
          Alleen de hoofdeigenaar kan demo-modus schakelen.
        </div>
      )}
    </Card>
  );
}

function OrderstatusSettings() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/settings/orderstatus").then(({ data }) => setItems(data || []));
  useEffect(() => { load(); }, []);
  const upsert = async (item) => {
    const { data } = await api.post("/settings/orderstatus", item);
    const n = data?.orders_recalculated ?? 0;
    toast.success(n > 0 ? `Opgeslagen · ${n} orders herberekend` : "Opgeslagen");
    load();
  };
  return (
    <Card className="bg-white border-slate-200 p-6 rounded-lg">
      <p className="text-sm text-slate-500 mb-5">
        Bepaal welke orderstatussen meetellen voor omzet en marge.
      </p>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.status_naam} className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0" data-testid={`status-row-${s.status_naam}`}>
            <Switch
              checked={s.telt_mee}
              onCheckedChange={(v) => upsert({ ...s, telt_mee: v })}
              data-testid={`status-switch-${s.status_naam}`}
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">{s.status_naam}</p>
              <p className="text-xs text-slate-500">{s.omschrijving}</p>
            </div>
            <span className={`text-xs px-2 py-1 rounded ${s.telt_mee ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {s.telt_mee ? "Telt mee" : "Uitgesloten"}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BetaalmethodesSettings() {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const load = () => api.get("/settings/betaalmethodes").then(({ data }) => setItems(data || []));
  useEffect(() => { load(); }, []);

  const save = async (item) => {
    const { data } = await api.post("/settings/betaalmethodes", item);
    const n = data?.orders_recalculated ?? 0;
    toast.success(n > 0 ? `Opgeslagen · ${n} orders herberekend` : "Opgeslagen");
    load();
  };
  const del = async (naam) => {
    if (!window.confirm("Verwijderen?")) return;
    const { data } = await api.delete(`/settings/betaalmethodes/${encodeURIComponent(naam)}`);
    const n = data?.orders_recalculated ?? 0;
    toast.success(n > 0 ? `Verwijderd · ${n} orders herberekend` : "Verwijderd");
    load();
  };

  return (
    <Card className="bg-white border-slate-200 p-6 rounded-lg">
      <p className="text-sm text-slate-500 mb-5">
        Map elke CSV-betaalmethode naar een kostregel (vaste kost + percentage).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="betaalmethodes-table">
          <thead>
            <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
              <th className="text-left py-2.5 px-3 font-semibold">Methode</th>
              <th className="text-right py-2.5 px-3 font-semibold">Vaste kost</th>
              <th className="text-right py-2.5 px-3 font-semibold">Percentage</th>
              <th className="text-center py-2.5 px-3 font-semibold">Actief</th>
              <th className="text-right py-2.5 px-3 font-semibold">Acties</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <BetaalmethodeRow key={b.csv_betaalmethode} item={b} onSave={save} onDelete={del} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-200 flex gap-2 items-end">
        <div className="flex-1">
          <Label>Nieuwe CSV-betaalmethode</Label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="vb. Mollie (SOFORT)" data-testid="new-betaalmethode-input" />
        </div>
        <Button onClick={() => { if (newName) { save({ csv_betaalmethode: newName, vaste_kost: 0, percentage_kost: 0, actief: true }); setNewName(""); } }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-betaalmethode-btn">
          <Plus className="h-4 w-4 mr-1" /> Toevoegen
        </Button>
      </div>
    </Card>
  );
}

function BetaalmethodeRow({ item, onSave, onDelete }) {
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item]);
  const isDirty = JSON.stringify(local) !== JSON.stringify(item);
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2.5 px-3 font-medium text-slate-900">{local.csv_betaalmethode}</td>
      <td className="py-2.5 px-3 text-right">
        <Input type="number" step="0.01" value={local.vaste_kost}
          onChange={(e) => setLocal({ ...local, vaste_kost: parseFloat(e.target.value) || 0 })}
          className="w-24 text-right ml-auto" />
      </td>
      <td className="py-2.5 px-3 text-right">
        <Input type="number" step="0.01" value={local.percentage_kost}
          onChange={(e) => setLocal({ ...local, percentage_kost: parseFloat(e.target.value) || 0 })}
          className="w-24 text-right ml-auto" />
      </td>
      <td className="py-2.5 px-3 text-center">
        <Switch checked={local.actief} onCheckedChange={(v) => setLocal({ ...local, actief: v })} />
      </td>
      <td className="py-2.5 px-3 text-right">
        <Button size="sm" variant={isDirty ? "default" : "ghost"} className={`h-8 mr-1 ${isDirty ? "bg-slate-900 hover:bg-slate-800" : ""}`} onClick={() => onSave(local)} data-testid={`save-betaalmethode-${item.csv_betaalmethode}`}>
          <Save className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onDelete(item.csv_betaalmethode)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function VasteKostenSettings() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState({ naam: "", bedrag_per_maand: 0, actief: true });
  const load = () => api.get("/settings/vaste-kosten").then(({ data }) => setItems(data || []));
  useEffect(() => { load(); }, []);

  const save = async (item) => {
    await api.post("/settings/vaste-kosten", item);
    toast.success("Opgeslagen");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Verwijderen?")) return;
    await api.delete(`/settings/vaste-kosten/${id}`);
    load();
  };

  const totaal = items.filter((i) => i.actief).reduce((sum, i) => sum + i.bedrag_per_maand, 0);

  return (
    <Card className="bg-white border-slate-200 p-6 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Worden afgetrokken van operationele marge per maand.</p>
        <div className="text-right">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Totaal/maand</p>
          <p className="text-2xl font-semibold text-slate-900">{formatCurrency(totaal)}</p>
        </div>
      </div>
      <table className="w-full text-sm" data-testid="vaste-kosten-table">
        <thead>
          <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
            <th className="text-left py-2.5 px-3 font-semibold">Naam</th>
            <th className="text-right py-2.5 px-3 font-semibold">Bedrag / maand</th>
            <th className="text-center py-2.5 px-3 font-semibold">Actief</th>
            <th className="text-right py-2.5 px-3 font-semibold">Acties</th>
          </tr>
        </thead>
        <tbody>
          {items.map((v) => <VasteKostRow key={v.id} item={v} onSave={save} onDelete={del} />)}
        </tbody>
      </table>
      <div className="mt-5 pt-5 border-t border-slate-200 flex gap-2 items-end">
        <div className="flex-1">
          <Label>Naam</Label>
          <Input value={adding.naam} onChange={(e) => setAdding({ ...adding, naam: e.target.value })} data-testid="new-vaste-kost-naam" />
        </div>
        <div>
          <Label>Bedrag / maand</Label>
          <Input type="number" step="0.01" value={adding.bedrag_per_maand} onChange={(e) => setAdding({ ...adding, bedrag_per_maand: parseFloat(e.target.value) || 0 })} className="w-32" data-testid="new-vaste-kost-bedrag" />
        </div>
        <Button onClick={() => { if (adding.naam) { save(adding); setAdding({ naam: "", bedrag_per_maand: 0, actief: true }); } }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-vaste-kost-btn">
          <Plus className="h-4 w-4 mr-1" /> Toevoegen
        </Button>
      </div>
    </Card>
  );
}

function VasteKostRow({ item, onSave, onDelete }) {
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item]);
  const isDirty = JSON.stringify(local) !== JSON.stringify(item);
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2.5 px-3">
        <Input value={local.naam} onChange={(e) => setLocal({ ...local, naam: e.target.value })} />
      </td>
      <td className="py-2.5 px-3 text-right">
        <Input type="number" step="0.01" value={local.bedrag_per_maand}
          onChange={(e) => setLocal({ ...local, bedrag_per_maand: parseFloat(e.target.value) || 0 })}
          className="w-32 text-right ml-auto" />
      </td>
      <td className="py-2.5 px-3 text-center">
        <Switch checked={local.actief} onCheckedChange={(v) => setLocal({ ...local, actief: v })} />
      </td>
      <td className="py-2.5 px-3 text-right">
        <Button size="sm" variant={isDirty ? "default" : "ghost"} className={`h-8 mr-1 ${isDirty ? "bg-slate-900 hover:bg-slate-800" : ""}`} onClick={() => onSave(local)}>
          <Save className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onDelete(item.id)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function BoekhoudkostSettings() {
  const [value, setValue] = useState(0.39);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/settings/boekhoudkost").then(({ data }) => { setValue(data.value); setLoaded(true); });
  }, []);
  const save = async () => {
    const { data } = await api.post("/settings/boekhoudkost", { value: parseFloat(value) });
    const n = data?.orders_recalculated ?? 0;
    toast.success(n > 0 ? `Boekhoudkost opgeslagen · ${n} orders herberekend` : "Boekhoudkost opgeslagen");
  };
  const recalc = async () => {
    if (!window.confirm("Herbereken alle marges met deze instelling?")) return;
    const { data } = await api.post("/settings/recalc");
    toast.success(`Herberekend: ${data.orders_updated} orders`);
  };

  return (
    <Card className="bg-white border-slate-200 p-6 rounded-lg">
      <p className="text-sm text-slate-500 mb-5">
        Vaste boekhoudkost per order (standaard €0,39). Wordt afgetrokken van bruto marge bij berekening van operationele marge.
      </p>
      <div className="flex items-end gap-3 max-w-md">
        <div className="flex-1">
          <Label>Boekhoudkost per order (€)</Label>
          <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} data-testid="boekhoudkost-input" disabled={!loaded} />
        </div>
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-boekhoudkost-btn">
          <Save className="h-4 w-4 mr-1.5" /> Opslaan
        </Button>
      </div>
      <div className="mt-6 pt-6 border-t border-slate-200">
        <Button variant="outline" onClick={recalc} data-testid="recalc-from-settings-btn">
          <RefreshCcw className="h-4 w-4 mr-1.5" />
          Herbereken alle marges
        </Button>
      </div>
    </Card>
  );
}
